import { FC, useCallback, useEffect, useRef, useState } from 'react';
import Typography from '@components/common/typography';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';
import TransactionTable from '@components/transactions_table';
import { getDateMonthAgo, getDisableFeature } from '@utils/index';
import RangeDatePicker from '@components/range_date_picker';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiWalletService } from '@api';
import { TCardTransaction } from '@xeppt/xeppt-sdk/types';

interface IProps {
    card_id?: string;
}

const TransactionsSection: FC<IProps> = ({ card_id }) => {
    const { t } = useTranslation('sections', {
        keyPrefix: 'xeppt_card.transaction'
    });
    const { dateNow, dateMonthAgo } = getDateMonthAgo();
    const [cursor, setCursor] = useState<Date>();
    const loader = useRef(null);
    const [transactions, setTransactions] = useState<TCardTransaction[]>([]);
    const [trigger, setTrigger] = useState(0);

    const [filter, setFilter] = useState<{ date_from: Date | null; date_to: Date | null }>({
        date_from: dateMonthAgo,
        date_to: dateNow
    });
    const {
        data: transactionsResponse,
        isLoading: isTransactionsLoading,
        handleRequest: handleGetTransactions
    } = useApiQuery({
        isInitialRequest: false,
        method: (request: any) => {
            return apiWalletService.getCardTransactions({
                start_date: filter?.date_from || new Date(),
                end_date: filter?.date_to || new Date(),
                card_id: card_id || '',
                order: 'DESC',
                cursor: request?.isNew
                    ? filter?.date_to || new Date()
                    : cursor || filter?.date_to || new Date(),
                limit: 10
            });
        },
        condition: !!card_id,
        deps: [filter, card_id],
        onSuccess: (data, requestData) => {
            if (!requestData?.isNew) {
                setTransactions((state) => [...state, ...(data?.data || [])]);
                setCursor(data?.cursor);
            } else {
                setTransactions(data?.data || []);
                setCursor(data?.cursor);
            }
        }
    });

    useEffect(() => {
        const loadItems = () => {
            if (
                transactions?.length < (transactionsResponse?.total || 0) &&
                !isTransactionsLoading
            ) {
                handleGetTransactions({ ...filter });
            }
        };

        loadItems();
    }, [trigger]);

    const handleObserver = useCallback((entries: any) => {
        const target = entries[0];
        if (target.isIntersecting) {
            setTrigger((prev) => prev + 1);
        }
    }, []);

    useEffect(() => {
        const option = {
            root: null,
            rootMargin: '20px',
            threshold: 0
        };
        const observer = new IntersectionObserver(handleObserver, option);
        if (loader.current) {
            observer.observe(loader.current);
        }
    }, [handleObserver]);

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <Typography variant="h4">{t('title')}</Typography>
                <div className={styles.range_date_wrapper}>
                    <RangeDatePicker
                        values={[
                            filter.date_from?.toString() || null,
                            filter.date_to?.toString() || null
                        ]}
                        maxDate={getDisableFeature()}
                        onChange={(val) => {
                            setFilter({
                                date_from: val?.[0] ? new Date(val?.[0] as string) : null,
                                date_to: val?.[1] ? new Date(val?.[1] as string) : null
                            });
                        }}
                        full
                    />
                </div>
            </div>
            <div className={styles.content}>
                <TransactionTable
                    activeTab="transactions"
                    transactions={transactions?.map((item) => item.transaction) || []}
                    isCard
                    transactionProvided
                    isTransactionsLoading={isTransactionsLoading}
                />
            </div>
        </div>
    );
};

export default TransactionsSection;
